import { useState, useEffect, useRef, useCallback } from 'react';

// Create a global state for focus flow
let globalStyleJustSelected = false;
let globalColorJustSelected = false;

// Function to reset the global focus flow state
export const resetGlobalFocusFlow = () => {
  globalStyleJustSelected = false;
  globalColorJustSelected = false;
};

/**
 * Custom hook to manage focus flow between style, color, and size components
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.refs - Object containing refs for each component
 * @param {Function} options.setStyleSearchMethods - Function to set style search methods
 * @param {Function} options.setColorSearchMethods - Function to set color search methods
 * @param {Object} options.state - Object containing state values
 * @param {boolean} options.state.isNewItem - Whether this is a newly created item
 * @param {boolean} options.state.isHotKeyCreated - Whether this item was created with Shift+Enter
 * @param {Array} options.state.availableColors - Available colors for the selected style
 * @param {Object} options.state.selectedStyle - The currently selected style
 * @param {string} options.state.selectedColor - The currently selected color
 * @param {boolean} options.state.resetPoppers - Whether to reset poppers
 * @returns {Object} - Methods to trigger focus transitions
 */
const useFocusFlow = ({ refs, setStyleSearchMethods, setColorSearchMethods, state }) => {
  const { styleRef, colorRef, sizesRef } = refs;
  const { 
    isNewItem, 
    isHotKeyCreated,
    availableColors, 
    selectedStyle, 
    selectedColor, 
    resetPoppers 
  } = state;
  
  // Track focus transition states - use the global state
  const [styleJustSelected, setStyleJustSelected] = useState(globalStyleJustSelected);
  const [colorJustSelected, setColorJustSelected] = useState(globalColorJustSelected);
  
  // Add a new state to track if we're in edit mode
  const [isEditingStyle, setIsEditingStyle] = useState(false);
  
  // Update global state when local state changes
  useEffect(() => {
    globalStyleJustSelected = styleJustSelected;
  }, [styleJustSelected]);
  
  useEffect(() => {
    globalColorJustSelected = colorJustSelected;
  }, [colorJustSelected]);
  
  // Create methods for style search component
  const createStyleSearchMethods = useCallback(() => {
    return {
      focus: () => {
        if (styleRef?.current) {
          styleRef.current.focus({ preventScroll: true });
        }
      },
      silentFocus: () => {
        if (styleRef?.current) {
          styleRef.current.focus({ preventScroll: true });
        }
      },
      closePopper: () => {
        if (styleRef?.current) {
          const inputElement = styleRef.current.querySelector('input');
          if (inputElement) {
            inputElement.blur();
          }
        }
      },
      // Add method to set edit mode
      setEditMode: (isEditing) => {
        setIsEditingStyle(isEditing);
      }
    };
  }, [styleRef]);
  
  // Create methods for color search component
  const createColorSearchMethods = useCallback(() => {
    return {
      focus: () => {
        // Only focus if not in edit mode
        if (!isEditingStyle && colorRef?.current) {
          colorRef.current.focus({ preventScroll: true });
        }
      },
      closePopper: () => {
        if (colorRef?.current) {
          const inputElement = colorRef.current.querySelector('input');
          if (inputElement) {
            inputElement.blur();
          }
        }
      }
    };
  }, [colorRef, isEditingStyle]);
  
  // Register methods with parent components
  useEffect(() => {
    if (setStyleSearchMethods) {
      setStyleSearchMethods(createStyleSearchMethods());
    }
    
    if (setColorSearchMethods) {
      setColorSearchMethods(createColorSearchMethods());
    }
    
    // Cleanup function
    return () => {
      if (setStyleSearchMethods) {
        setStyleSearchMethods(null);
      }
      if (setColorSearchMethods) {
        setColorSearchMethods(null);
      }
    };
  }, [setStyleSearchMethods, setColorSearchMethods, createStyleSearchMethods, createColorSearchMethods]);
  
  // Effect to focus style search when this is a new item or created with hotkey
  useEffect(() => {
    // Give priority to hotkey created items
    if (isHotKeyCreated && styleRef?.current) {
      // Use a slightly longer delay for hotkey created items to ensure the component is fully rendered
      setTimeout(() => {
        if (styleRef.current) {
          // Try to directly focus the input element
          const inputElement = styleRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            // Also select all text in the input
            inputElement.select();
          } else if (styleRef.current.focus) {
            styleRef.current.focus();
          }
        }
      }, 100);
    } else if (isNewItem && styleRef?.current) {
      // Focus the style search bar but don't open the popper
      setTimeout(() => {
        if (styleRef.current.silentFocus) {
          styleRef.current.silentFocus();
        } else if (styleRef.current.focus) {
          styleRef.current.focus();
        }
      }, 50);
    }
  }, [isNewItem, isHotKeyCreated, styleRef]);
  
  // Effect to focus color search when a style is selected
  useEffect(() => {
    if (styleJustSelected && colorRef?.current && availableColors.length > 0 && !isEditingStyle) {
      // Focus the color search only if not in edit mode
      colorRef.current.focus();
      // Reset the flag
      setStyleJustSelected(false);
    }
  }, [styleJustSelected, availableColors, colorRef, isEditingStyle]);

  //  // Effect to focus sizes when a color is selected
  // useEffect(() => {
  //   if (colorJustSelected && sizesRef?.current && selectedColor) {
  //     // Focus the sizes component
  //     sizesRef.current.focus();
  //     // Reset the flag
  //     setColorJustSelected(false);
  //   }
  // }, [colorJustSelected, selectedColor, sizesRef]);
  
  // Effect to handle popper reset
  useEffect(() => {
    if (resetPoppers) {
      // Close any open poppers
      if (styleRef?.current?.closePopper) {
        styleRef.current.closePopper();
      }
      if (colorRef?.current?.closePopper) {
        colorRef.current.closePopper();
      }
    }
  }, [resetPoppers, styleRef, colorRef]);
  
  // Methods to trigger focus transitions
  const triggerStyleSelected = () => {
    if (!isEditingStyle) {
      setStyleJustSelected(true);
      setColorJustSelected(false);
    }
  };
  
  const triggerColorSelected = () => {
    setColorJustSelected(true);
    setStyleJustSelected(false);
  };
  
  // Add a function to reset the focus flow state
  const resetFocusFlow = useCallback(() => {
    setStyleJustSelected(false);
    setColorJustSelected(false);
    // Also reset the global state
    globalStyleJustSelected = false;
    globalColorJustSelected = false;
  }, []);
  
  return {
    triggerStyleSelected,
    triggerColorSelected,
    styleJustSelected,
    colorJustSelected,
    resetFocusFlow,
    setEditMode: setIsEditingStyle
  };
};

export default useFocusFlow;